import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Reusable async-messaging thread (sub-spec 01c Plan 6).
 *
 * Three contexts:
 *   - `customer-account` — signed-in customer on `/account/bookings/:id`. Reads
 *     + writes via `/api/customer/bookings/:bookingId/messages`.
 *   - `staff` — staff member on the booking detail / today page. Reads + writes
 *     via `/api/bookings/:bookingId/messages`. Supports moderate-delete.
 *   - `public-stay` — unauthenticated visitor on `/stay/<token>`. Read-only
 *     for v1 (write surface comes when share-token sessions widen — until
 *     then the visitor uses the customer-account thread).
 *
 * Realtime: parent drives re-fetch via `realtimeNonce`. The component never
 * subscribes itself; the host (BookingDetail / LiveStayPage / staff page)
 * owns the AccountRealtimeDO socket and bumps the nonce on
 * BOOKING_MESSAGE_POSTED events for the matching bookingId.
 *
 * Mark-read: when the thread mounts (and any time the user re-opens it),
 * we fire-and-forget POST `mark-read` so the other side's unread counter
 * decrements. Soft-deleted messages render as "(message removed)".
 */

export type MessagesThreadContext = 'customer-account' | 'staff' | 'public-stay';

export interface MessagesThreadProps {
  bookingId: string;
  context: MessagesThreadContext;
  /** Required when context='public-stay'. */
  shareToken?: string | undefined;
  /** Bump to force a refetch on realtime events. */
  realtimeNonce?: number | undefined;
  /**
   * Staff-side: caller passes capability check result so the moderate-delete
   * button only shows up for users who actually have the cap. Defaults false.
   */
  canModerate?: boolean | undefined;
}

export interface BookingMessage {
  id: string;
  bookingId?: string;
  sender: 'customer' | 'staff';
  body: string;
  attachmentFileIds: string[];
  createdAt: string;
  readAt?: string | null;
  deletedAt?: string | null;
  deleted?: boolean;
}

function listEndpoint(props: MessagesThreadProps): string {
  if (props.context === 'public-stay') {
    if (!props.shareToken) throw new Error('MessagesThread: shareToken required for public-stay');
    return `/api/public/stay/${encodeURIComponent(props.shareToken)}/messages`;
  }
  if (props.context === 'customer-account') {
    return `/api/customer/bookings/${encodeURIComponent(props.bookingId)}/messages`;
  }
  return `/api/bookings/${encodeURIComponent(props.bookingId)}/messages`;
}

function postEndpoint(props: MessagesThreadProps): string {
  return listEndpoint(props);
}

function markReadEndpoint(props: MessagesThreadProps): string | null {
  if (props.context === 'public-stay') return null;
  if (props.context === 'customer-account') {
    return `/api/customer/bookings/${encodeURIComponent(props.bookingId)}/messages/mark-read`;
  }
  return `/api/bookings/${encodeURIComponent(props.bookingId)}/messages/mark-read`;
}

function deleteEndpoint(messageId: string): string {
  // Staff-only moderation route.
  return `/api/messages/${encodeURIComponent(messageId)}`;
}

function presignEndpoint(props: MessagesThreadProps): string | null {
  if (props.context === 'public-stay') return null;
  // Both customer + staff use the staff-side presigned-upload route. The
  // entity_type gate (booking_message_attachment) is permissive (`either`).
  return '/api/files/presigned-upload';
}

function confirmEndpoint(fileId: string): string {
  return `/api/files/${encodeURIComponent(fileId)}/complete`;
}

const fmtTime = (iso: string) => {
  try {
    return new Date(iso).toLocaleString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
      day: 'numeric',
      month: 'short',
    });
  } catch {
    return iso;
  }
};

const MAX_BODY = 2000;
const MAX_ATTACHMENTS = 5;

export function MessagesThread(props: MessagesThreadProps) {
  const [messages, setMessages] = useState<BookingMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const fetchMessages = useCallback(async () => {
    try {
      const url = listEndpoint(props);
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) {
        setError(`Could not load messages (${res.status})`);
        setLoading(false);
        return;
      }
      const body = (await res.json()) as { messages: BookingMessage[] };
      // The API returns newest-first; we want oldest-first for the
      // bottom-anchored thread view.
      const ordered = (body.messages ?? []).slice().reverse();
      setMessages(ordered);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [props.bookingId, props.context, props.shareToken]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setLoading(true);
    fetchMessages();
  }, [fetchMessages, props.realtimeNonce]);

  // Mark-read on mount + every realtime bump (the *other* side just posted).
  useEffect(() => {
    const url = markReadEndpoint(props);
    if (!url) return;
    fetch(url, { method: 'POST', credentials: 'include' }).catch(() => {
      // Best-effort; mark-read failure is non-fatal.
    });
  }, [props.context, props.bookingId, props.realtimeNonce]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-scroll to bottom on new messages.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages.length]);

  const handleSend = useCallback(
    async (body: string, attachmentFileIds: string[]) => {
      try {
        const res = await fetch(postEndpoint(props), {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ body, attachmentFileIds }),
        });
        if (!res.ok) {
          const err = (await res.json().catch(() => ({}))) as { error?: string };
          throw new Error(err.error ?? `send failed (${res.status})`);
        }
        await fetchMessages();
      } catch (e) {
        throw e;
      }
    },
    [props, fetchMessages],
  );

  const handleDelete = useCallback(
    async (messageId: string) => {
      try {
        const res = await fetch(deleteEndpoint(messageId), {
          method: 'DELETE',
          credentials: 'include',
        });
        if (!res.ok) return;
        await fetchMessages();
      } catch {
        /* swallow — best effort */
      }
    },
    [fetchMessages],
  );

  return (
    <div data-testid="messages-thread">
      <div
        ref={scrollRef}
        data-testid="messages-thread-list"
        style={{
          maxHeight: '420px',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.65rem',
          padding: '0.5rem 0',
        }}
      >
        {loading && messages.length === 0 && (
          <p
            style={{ fontSize: '0.85rem', color: 'var(--muted-foreground, oklch(0.52 0.008 75))' }}
          >
            Loading…
          </p>
        )}
        {error && (
          <p
            role="alert"
            style={{ fontSize: '0.85rem', color: 'var(--destructive, oklch(0.55 0.15 30))' }}
          >
            {error}
          </p>
        )}
        {!loading && messages.length === 0 && !error && (
          <p
            data-testid="messages-thread-empty"
            style={{ fontSize: '0.85rem', color: 'var(--muted-foreground, oklch(0.52 0.008 75))' }}
          >
            No messages yet.
          </p>
        )}
        {messages.map((m) => (
          <MessageBubble
            key={m.id}
            message={m}
            context={props.context}
            canModerate={props.canModerate ?? false}
            onDelete={handleDelete}
          />
        ))}
      </div>
      {props.context === 'public-stay' ? (
        <p
          style={{
            marginTop: '0.5rem',
            fontSize: '0.75rem',
            color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
          }}
        >
          Sign in to your account to reply.
        </p>
      ) : (
        <Composer
          onSend={handleSend}
          presignUrl={presignEndpoint(props)}
          confirmUrlFor={confirmEndpoint}
          bookingId={props.bookingId}
        />
      )}
    </div>
  );
}

function MessageBubble({
  message,
  context,
  canModerate,
  onDelete,
}: {
  message: BookingMessage;
  context: MessagesThreadContext;
  canModerate: boolean;
  onDelete: (id: string) => void;
}) {
  const isStaff = message.sender === 'staff';
  const viewerIsStaff = context === 'staff';
  const isOwn = viewerIsStaff ? isStaff : !isStaff;
  const isDeleted = !!(message.deletedAt || message.deleted);

  const align = isOwn ? 'flex-end' : 'flex-start';
  const bg = isOwn
    ? 'var(--primary, oklch(0.48 0.10 250))'
    : 'var(--secondary, oklch(0.95 0.005 75))';
  const fg = isOwn ? 'white' : 'inherit';

  return (
    <div
      data-testid={`message-${message.id}`}
      data-sender={message.sender}
      style={{ display: 'flex', justifyContent: align }}
    >
      <div
        style={{
          maxWidth: '78%',
          background: isDeleted ? 'transparent' : bg,
          color: fg,
          border: isDeleted ? '1px dashed var(--border, oklch(0.85 0.005 75))' : 'none',
          padding: '0.55rem 0.85rem',
          borderRadius: '14px',
          fontSize: '0.92rem',
        }}
      >
        {isDeleted ? (
          <span
            style={{
              fontStyle: 'italic',
              color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
            }}
            data-testid={`message-deleted-${message.id}`}
          >
            (message removed)
          </span>
        ) : (
          <>
            <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{message.body}</div>
            {message.attachmentFileIds.length > 0 && (
              <div
                style={{
                  marginTop: '0.4rem',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))',
                  gap: '0.35rem',
                }}
                data-testid={`message-attachments-${message.id}`}
              >
                {message.attachmentFileIds.map((fid) => (
                  <a
                    key={fid}
                    href={`/api/files/${encodeURIComponent(fid)}/url?variant=full`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ display: 'block' }}
                  >
                    <img
                      src={`/api/files/${encodeURIComponent(fid)}/url?variant=thumb`}
                      alt="attachment"
                      loading="lazy"
                      style={{
                        width: '100%',
                        aspectRatio: '1 / 1',
                        objectFit: 'cover',
                        borderRadius: '8px',
                        background: 'oklch(0.95 0.005 75)',
                      }}
                    />
                  </a>
                ))}
              </div>
            )}
          </>
        )}
        <div
          style={{
            marginTop: '0.3rem',
            fontSize: '0.7rem',
            opacity: 0.7,
            display: 'flex',
            justifyContent: 'space-between',
            gap: '0.5rem',
          }}
        >
          <span className="num">{fmtTime(message.createdAt)}</span>
          {viewerIsStaff && !isDeleted && canModerate && (
            <button
              type="button"
              data-testid={`message-delete-${message.id}`}
              onClick={() => onDelete(message.id)}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: isOwn ? 'white' : 'inherit',
                fontSize: '0.7rem',
                opacity: 0.85,
                padding: 0,
              }}
            >
              Delete
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

interface ComposerProps {
  onSend: (body: string, attachmentFileIds: string[]) => Promise<void>;
  presignUrl: string | null;
  confirmUrlFor: (fileId: string) => string;
  bookingId: string;
}

function Composer({ onSend, presignUrl, confirmUrlFor, bookingId }: ComposerProps) {
  const [body, setBody] = useState('');
  const [attachments, setAttachments] = useState<string[]>([]);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const counterColour =
    body.length > MAX_BODY ? 'var(--destructive, oklch(0.55 0.15 30))' : 'inherit';

  const submitDisabled =
    sending ||
    uploading ||
    body.length > MAX_BODY ||
    (body.trim().length === 0 && attachments.length === 0);

  async function handleSend() {
    setError(null);
    setSending(true);
    try {
      await onSend(body.trim(), attachments);
      setBody('');
      setAttachments([]);
    } catch (e) {
      setError((e as Error).message ?? 'Send failed.');
    } finally {
      setSending(false);
    }
  }

  async function handleFile(file: File) {
    if (!presignUrl) return;
    if (attachments.length >= MAX_ATTACHMENTS) {
      setError(`Up to ${MAX_ATTACHMENTS} attachments per message.`);
      return;
    }
    setError(null);
    setUploading(true);
    try {
      const presignRes = await fetch(presignUrl, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entityType: 'booking_message_attachment',
          entityId: bookingId,
          contentType: file.type || 'application/octet-stream',
          contentLength: file.size,
          originalFileName: file.name,
        }),
      });
      if (!presignRes.ok) {
        const ebody = (await presignRes.json().catch(() => ({}))) as { error?: string };
        throw new Error(ebody.error ?? `presign failed (${presignRes.status})`);
      }
      const presign = (await presignRes.json()) as {
        fileId: string;
        uploadUrl: string;
        requiredHeaders?: Record<string, string>;
      };

      const putRes = await fetch(presign.uploadUrl, {
        method: 'PUT',
        headers: presign.requiredHeaders ?? {},
        body: file,
      });
      if (!putRes.ok) throw new Error(`upload failed (${putRes.status})`);

      const confirmRes = await fetch(confirmUrlFor(presign.fileId), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileId: presign.fileId }),
      });
      if (!confirmRes.ok) {
        const ebody = (await confirmRes.json().catch(() => ({}))) as { error?: string };
        throw new Error(ebody.error ?? `confirm failed (${confirmRes.status})`);
      }
      setAttachments((prev) => [...prev, presign.fileId]);
    } catch (e) {
      setError((e as Error).message ?? 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  return (
    <div
      data-testid="messages-thread-composer"
      style={{
        marginTop: '0.75rem',
        padding: '0.65rem',
        background: 'var(--surface, white)',
        border: '1px solid var(--border, oklch(0.91 0.005 80))',
        borderRadius: '12px',
      }}
    >
      <textarea
        data-testid="composer-body"
        rows={3}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Write a message…"
        disabled={sending}
        style={{
          width: '100%',
          border: 'none',
          background: 'transparent',
          outline: 'none',
          fontSize: '0.95rem',
          resize: 'vertical',
          fontFamily: 'inherit',
        }}
      />
      {attachments.length > 0 && (
        <div
          data-testid="composer-attachments"
          style={{
            display: 'flex',
            gap: '0.4rem',
            flexWrap: 'wrap',
            marginTop: '0.35rem',
          }}
        >
          {attachments.map((fid) => (
            <div
              key={fid}
              data-testid={`composer-attachment-${fid}`}
              style={{
                position: 'relative',
                width: '54px',
                height: '54px',
                borderRadius: '8px',
                overflow: 'hidden',
                border: '1px solid var(--border, oklch(0.91 0.005 80))',
              }}
            >
              <img
                src={`/api/files/${encodeURIComponent(fid)}/url?variant=thumb`}
                alt="attachment"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
              <button
                type="button"
                data-testid={`composer-attachment-remove-${fid}`}
                onClick={() => setAttachments((prev) => prev.filter((id) => id !== fid))}
                style={{
                  position: 'absolute',
                  top: '2px',
                  right: '2px',
                  width: '18px',
                  height: '18px',
                  borderRadius: '50%',
                  border: 'none',
                  background: 'rgba(0,0,0,0.6)',
                  color: 'white',
                  fontSize: '0.65rem',
                  cursor: 'pointer',
                }}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
      {error && (
        <p
          role="alert"
          data-testid="composer-error"
          style={{
            margin: '0.4rem 0 0',
            fontSize: '0.75rem',
            color: 'var(--destructive, oklch(0.55 0.15 30))',
          }}
        >
          {error}
        </p>
      )}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginTop: '0.45rem',
          gap: '0.5rem',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
          {presignUrl && (
            <label
              data-testid="composer-attach-label"
              style={{
                cursor: uploading ? 'not-allowed' : 'pointer',
                fontSize: '0.78rem',
                color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
                opacity: uploading ? 0.6 : 1,
              }}
            >
              {uploading ? 'Uploading…' : 'Attach'}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/heic,application/pdf"
                disabled={uploading || attachments.length >= MAX_ATTACHMENTS}
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void handleFile(file);
                }}
              />
            </label>
          )}
          <span data-testid="composer-counter" style={{ fontSize: '0.7rem', color: counterColour }}>
            {body.length}/{MAX_BODY}
          </span>
        </div>
        <button
          type="button"
          data-testid="composer-send"
          onClick={() => void handleSend()}
          disabled={submitDisabled}
          style={{
            padding: '0.45rem 1rem',
            borderRadius: '10px',
            border: 'none',
            background: 'var(--primary, oklch(0.48 0.10 250))',
            color: 'white',
            fontSize: '0.85rem',
            fontWeight: 500,
            cursor: submitDisabled ? 'not-allowed' : 'pointer',
            opacity: submitDisabled ? 0.5 : 1,
          }}
        >
          {sending ? 'Sending…' : 'Send'}
        </button>
      </div>
    </div>
  );
}
